import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { ScrollText, Save, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Representatives from "@/components/legal/Representatives";
import CorporateDocuments from "@/components/legal/CorporateDocuments";
import RequirementTemplates from "@/components/legal/RequirementTemplates";
import Letterheads from "@/components/legal/Letterheads";
import Oficios from "@/components/legal/Oficios";

const TABS = [
  { id: "perfil", label: "Expediente Corporativo" },
  { id: "representantes", label: "Representantes Legales" },
  { id: "documentos", label: "Documentos" },
  { id: "oficios", label: "Oficios" },
  { id: "membretes", label: "Membretes" },
  { id: "plantillas", label: "Plantillas de Requisitos" },
];

const EMPTY_BANK = { banco: "", clabe: "", cuenta: "", titular: "" };

function ProfileTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/legal/profile").then((r) => setForm(r.data)).catch(() => toast.error("No se pudo cargar el perfil"));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setBank = (i, k, v) => setForm((f) => {
    const banks = [...(f.bank_accounts || [])];
    banks[i] = { ...banks[i], [k]: v };
    return { ...f, bank_accounts: banks };
  });

  const save = async () => {
    setSaving(true);
    try {
      const { id, updated_at, updated_by, ...payload } = form;
      await api.put("/legal/profile", payload);
      toast.success("Expediente corporativo guardado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="p-8 text-center text-slate-400">Cargando…</div>;

  const field = (label, k) => (
    <div key={k}>
      <label className="label-mono block mb-1">{label}</label>
      <input data-testid={`legal-profile-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)}
        className="w-full input-cyber" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="legal-profile-tab">
      <div className="panel p-4">
        <div className="label-mono mb-3">// DATOS FISCALES</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {field("Razón social", "razon_social")}
          {field("RFC", "rfc")}
          {field("Régimen fiscal", "regimen_fiscal")}
          {field("Domicilio fiscal", "domicilio_fiscal")}
        </div>
      </div>
      <div className="panel p-4">
        <div className="label-mono mb-3">// ACTA CONSTITUTIVA Y DATOS NOTARIALES</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {field("Notario", "notario")}
          {field("Notaría No.", "notaria_numero")}
          {field("Instrumento No.", "instrumento_numero")}
          <div>
            <label className="label-mono block mb-1">Fecha de constitución</label>
            <input type="date" data-testid="legal-profile-fecha_constitucion" value={form.fecha_constitucion || ""}
              onChange={(e) => set("fecha_constitucion", e.target.value)} className="w-full input-cyber" />
          </div>
          {field("Folio mercantil (Registro Público)", "folio_mercantil")}
          {field("Registro Público de la Propiedad", "registro_publico")}
        </div>
      </div>
      <div className="panel p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="label-mono">// DATOS BANCARIOS AUTORIZADOS</div>
          <button data-testid="legal-add-bank-btn" onClick={() => set("bank_accounts", [...(form.bank_accounts || []), { ...EMPTY_BANK }])}
            className="btn-ghost px-2 py-1 text-xs flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Agregar cuenta</button>
        </div>
        {(form.bank_accounts || []).length === 0 && (
          <div className="text-xs text-slate-500 italic">Sin cuentas registradas.</div>
        )}
        <div className="space-y-2">
          {(form.bank_accounts || []).map((b, i) => (
            <div key={i} className="grid grid-cols-2 md:grid-cols-5 gap-2 items-center">
              <input placeholder="Banco" value={b.banco} onChange={(e) => setBank(i, "banco", e.target.value)} className="input-cyber" data-testid={`legal-bank-banco-${i}`} />
              <input placeholder="CLABE" value={b.clabe} onChange={(e) => setBank(i, "clabe", e.target.value)} className="input-cyber" />
              <input placeholder="Cuenta" value={b.cuenta} onChange={(e) => setBank(i, "cuenta", e.target.value)} className="input-cyber" />
              <input placeholder="Titular" value={b.titular} onChange={(e) => setBank(i, "titular", e.target.value)} className="input-cyber" />
              <button onClick={() => set("bank_accounts", form.bank_accounts.filter((_, j) => j !== i))}
                className="text-slate-500 hover:text-red-400 justify-self-start"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="panel p-4">
        <div className="label-mono mb-2">// NOTAS</div>
        <textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={3}
          className="w-full input-cyber" data-testid="legal-profile-notes" />
      </div>
      <button data-testid="legal-profile-save-btn" onClick={save} disabled={saving}
        className="btn-primary px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar expediente
      </button>
    </div>
  );
}

export default function LegalRecords() {
  const [tab, setTab] = useState("perfil");
  const [summary, setSummary] = useState(null);

  const loadSummary = () => api.get("/legal/summary").then((r) => setSummary(r.data)).catch(() => {});
  useEffect(() => { loadSummary(); }, [tab]);

  return (
    <div data-testid="legal-records-page">
      <PageHeader title="Gestión Documental" subtitle="EXPEDIENTE JURÍDICO, REPRESENTANTES, DOCUMENTOS Y PLANTILLAS DE REQUISITOS" />
      <div className="px-6 lg:px-8 py-5 space-y-5">
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="legal-summary-kpis">
            {[["Documentos", summary.documentos], ["Vigentes", summary.vigentes],
              ["Por vencer (30d)", summary.por_vencer_30d], ["Vencidos", summary.vencidos],
              ["Representantes", summary.representantes_vigentes]].map(([l, v]) => (
              <div key={l} className="panel p-3">
                <div className="label-mono text-[10px]">{l}</div>
                <div className={`font-mono text-lg font-bold ${l === "Vencidos" && v > 0 ? "text-red-400" : l.startsWith("Por vencer") && v > 0 ? "text-amber-400" : "text-cyan-300"}`}>{v}</div>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-1 flex-wrap border-b pb-2" style={{ borderColor: "var(--border)" }}>
          {TABS.map((t) => (
            <button key={t.id} data-testid={`legal-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest rounded-sm ${tab === t.id ? "btn-primary" : "btn-ghost"}`}>
              {t.label}
            </button>
          ))}
        </div>
        {tab === "perfil" && <ProfileTab />}
        {tab === "representantes" && <Representatives onMutate={loadSummary} />}
        {tab === "documentos" && <CorporateDocuments onMutate={loadSummary} />}
        {tab === "oficios" && <Oficios />}
        {tab === "membretes" && <Letterheads />}
        {tab === "plantillas" && <RequirementTemplates />}
      </div>
    </div>
  );
}
